import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Card from '@amzn/meridian/card';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Icon from '@amzn/meridian/icon';
import plusKnockoutTokens from '@amzn/meridian-tokens/base/icon/plus-knockout';
import minusKnockoutTokens from '@amzn/meridian-tokens/base/icon/minus-knockout';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import { SUCCESS_COLOR, ERROR_COLOR, BLUE_COLOR } from '@constants/values';
import { AgileTableRow } from '@components/agile/types/AgileTableRow';

interface AgileManagementModalsProps {
  openConfirmationModal: boolean;
  successful: boolean;
  swapErrorMsg?: string;
  onCloseModal: () => void;
  onConfirmSwap: () => void;
  getChangesForReview: () => {
    newAsins: AgileTableRow[];
    modifiedAsins: AgileTableRow[];
    deletedAsins: AgileTableRow[];
  };
}

const AgileManagementModals: React.FC<AgileManagementModalsProps> = ({
  openConfirmationModal,
  successful,
  swapErrorMsg,
  onCloseModal,
  onConfirmSwap,
  getChangesForReview,
}) => {
  const { newAsins, modifiedAsins, deletedAsins } = getChangesForReview();

  return (
    <>
      {/* Confirmation Modal */}
      <Modal open={openConfirmationModal} scrollContainer="viewport" width="800" aria-describedby="modal-description">
        <Alert size="large" type="warning">
          Please review and confirm the changes.
        </Alert>
        <Row alignmentHorizontal="center" spacingInset="300" spacing="400">
          <Card>
            <Table headerRows={1} spacing="small">
              <TableRow>
                <TableCell alignmentHorizontal="center">ASIN</TableCell>
                <TableCell alignmentHorizontal="center">Title</TableCell>
                <TableCell alignmentHorizontal="center">Zone</TableCell>
                <TableCell alignmentHorizontal="center">Change</TableCell>
                <TableCell alignmentHorizontal="center">Facings</TableCell>
                <TableCell alignmentHorizontal="center">Subcategory</TableCell>
              </TableRow>
              {[
                ...newAsins.map((item) => (
                  <TableRow key={`new-${item.asin}-${item.zoneName}`}>
                    <TableCell alignmentHorizontal="center">{item.asin}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.title}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.zoneName}</TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text className={SUCCESS_COLOR}>
                        <Icon tokens={plusKnockoutTokens} />
                      </Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">{item.remainingFacings}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.subcategoryId}</TableCell>
                  </TableRow>
                )),
                ...modifiedAsins.map((item) => (
                  <TableRow key={`mod-${item.asin}-${item.zoneName}`}>
                    <TableCell alignmentHorizontal="center">{item.asin}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.title}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.zoneName}</TableCell>
                    <TableCell alignmentHorizontal="center">
                      {item.remainingFacings === 0 ? (
                        <Text className={ERROR_COLOR}>
                          <Icon tokens={closeKnockoutTokens} />
                        </Text>
                      ) : item.difference < 0 ? (
                        <Text className={BLUE_COLOR}>
                          <Icon tokens={minusKnockoutTokens} />
                        </Text>
                      ) : (
                        <Text className={SUCCESS_COLOR}>
                          <Icon tokens={plusKnockoutTokens} />
                        </Text>
                      )}
                    </TableCell>
                    <TableCell alignmentHorizontal="center">{item.remainingFacings}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.subcategoryId}</TableCell>
                  </TableRow>
                )),
                ...deletedAsins.map((item) => (
                  <TableRow key={`del-${item.asin}-${item.zoneName}`}>
                    <TableCell alignmentHorizontal="center">{item.asin}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.title}</TableCell>
                    <TableCell alignmentHorizontal="center">{item.zoneName}</TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text className={ERROR_COLOR}>
                        <Icon tokens={closeKnockoutTokens} />
                      </Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">0</TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text>{String(item.subcategoryId || 'N/A')}</Text>
                    </TableCell>
                  </TableRow>
                )),
              ]}
            </Table>
          </Card>
        </Row>

        {swapErrorMsg && (
          <Alert type="error" size="large">
            {swapErrorMsg}
          </Alert>
        )}

        <ModalFooter>
          <Row alignmentHorizontal="center" widths="fit">
            <Button type="secondary" size="large" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button type="primary" size="large" onClick={onConfirmSwap}>
              Submit
            </Button>
          </Row>
        </ModalFooter>
      </Modal>

      {/* Success Modal */}
      <Modal open={successful} scrollContainer="viewport" aria-describedby="modal-description" onClose={onCloseModal}>
        <Alert type="success" size="xlarge">
          Changes Submitted Successfully
        </Alert>
      </Modal>
    </>
  );
};

export default AgileManagementModals;
